import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import fallbackHeroImage from '../../assets/images/heroimage.png';
import { productImages } from '../../services/storefront';
import { openAuth } from '../storefront/storefrontEvents';
import AccentText from '../storefront/AccentText';
import { IconArrowRight, IconCheck, Longhorn } from '../storefront/icons';

// Shown until the hero_section asset loads, and if it has no copy.
const HERO_FALLBACK = {
  title: 'Cowhides, Furniture & Home Decor',
  description:
    'Discover our curated collection of unique, high-quality goods. From authentic hides to handcrafted furniture, elevate your space with elegance.',
};

const pad = (n) => String(n).padStart(2, '0');

const RotatingBadge = () => (
  <div className="tp-badge" aria-hidden="true">
    <svg className="tp-badge__ring" viewBox="0 0 200 200">
      <defs>
        <path id="tp-badge-path" d="M100 100m-74 0a74 74 0 1 1 148 0a74 74 0 1 1 -148 0" />
      </defs>
      <text className="tp-badge__text">
        <textPath href="#tp-badge-path" textLength="462" lengthAdjust="spacing">
          BS Trading Co. • Wholesale • Burleson, Texas •
        </textPath>
      </text>
    </svg>
    <Longhorn className="tp-badge__mark" />
  </div>
);

// Floating card that cycles through the admin's featured products. The CSS
// progress bar drives the timing: when it finishes, we advance. Hover pauses
// it, and with reduced motion the bar (and so the cycling) is disabled.
const NowFeaturing = ({ products, label, cta }) => {
  const [index, setIndex] = useState(0);
  const product = products[index % products.length];
  const image = productImages(product)[0];

  return (
    <Link to={product.link_url || '/products'} className="tp-now">
      <span className="tp-now__thumb">
        {image && <img key={product.id} src={image} alt="" decoding="async" />}
      </span>
      <span className="tp-now__body">
        <span className="tp-now__label">
          <span data-copy-key="hero.featuringLabel">{label}</span>
          <span className="tp-now__index">{pad(index + 1)} / {pad(products.length)}</span>
        </span>
        <span className="tp-now__title" key={`title-${product.id}`}>{product.title}</span>
        <span className="tp-now__cta" data-copy-key="hero.featuringCta">
          {cta} <IconArrowRight size={14} />
        </span>
      </span>
      {products.length > 1 && (
        <span
          className="tp-now__progress"
          key={`progress-${index}`}
          onAnimationEnd={() => setIndex((current) => (current + 1) % products.length)}
        />
      )}
    </Link>
  );
};

const HeroSection = ({ hero, status, featuredProducts, stats, tradeStatus, firstName, copy }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);

  const title = hero?.title || HERO_FALLBACK.title;
  const description = hero?.description || HERO_FALLBACK.description;
  // Wait for the live hero instead of flashing the bundled one; fall back to
  // it if the homepage data couldn't load, has no hero image, or it 404s.
  const liveImage = imageFailed ? null : hero?.image_url;
  const imageUrl = liveImage || (status === 'loading' ? null : fallbackHeroImage);

  const eyebrow = tradeStatus.state !== 'guest' && firstName
    ? `Welcome back, ${firstName}`
    : copy['hero.eyebrow'];

  let secondaryAction;
  if (tradeStatus.state === 'guest') {
    secondaryAction = (
      <button type="button" className="tp-btn tp-btn--ghost" onClick={() => openAuth('register')} data-copy-key="hero.applyCta">
        {copy['hero.applyCta']}
      </button>
    );
  } else if (tradeStatus.state === 'pending') {
    secondaryAction = (
      <Link to="/dashboard" className="tp-btn tp-btn--ghost">Trade approval pending</Link>
    );
  } else {
    secondaryAction = (
      <Link to="/dashboard" className="tp-btn tp-btn--ghost">Your orders</Link>
    );
  }

  return (
    <section className="tp-hero" aria-labelledby="tp-hero-title">
      <div className="tp-hero__inner tp-container">
        <div className="tp-hero__copy">
          <span className="tp-eyebrow tp-rise" style={{ '--d': 0 }} data-copy-key="hero.eyebrow">{eyebrow}</span>
          <h1 id="tp-hero-title" className="tp-display tp-hero__title tp-rise" style={{ '--d': 1 }} data-copy-key="banner.title">
            <AccentText text={title} />
          </h1>
          <p className="tp-hero__lede tp-rise" style={{ '--d': 2 }} data-copy-key="banner.description">{description}</p>
          <div className="tp-hero__actions tp-rise" style={{ '--d': 3 }}>
            <Link to="/products" className="tp-btn tp-btn--icon" data-copy-key="hero.primaryCta">
              {copy['hero.primaryCta']}
              <span className="tp-btn__icon"><IconArrowRight size={18} /></span>
            </Link>
            {secondaryAction}
          </div>
          <div className="tp-hero__meta tp-rise" style={{ '--d': 4 }}>
            <dl className="tp-hero__stats">
              <div className="tp-stat">
                <dt>Collections</dt>
                <dd>{stats.collections || '—'}</dd>
              </div>
              <div className="tp-stat">
                <dt>Categories</dt>
                <dd>{stats.categories || '—'}</dd>
              </div>
            </dl>
            <p className="tp-hero__pill" data-copy-key="hero.badge">
              <IconCheck size={16} />
              {tradeStatus.state === 'active' ? 'Trade account approved' : copy['hero.badge']}
            </p>
          </div>
        </div>

        <div className="tp-hero__visual">
          <div className={`tp-hero__arch${imageLoaded ? ' is-loaded' : ''}`} data-copy-key="banner.image">
            {imageUrl && (
              <img
                src={imageUrl}
                alt=""
                className="tp-hero__img"
                fetchPriority="high"
                decoding="async"
                onLoad={() => setImageLoaded(true)}
                onError={() => {
                  if (imageUrl !== fallbackHeroImage) setImageFailed(true);
                }}
              />
            )}
          </div>
          <RotatingBadge />
          {featuredProducts.length > 0 && (
            <NowFeaturing
              products={featuredProducts}
              label={copy['hero.featuringLabel']}
              cta={copy['hero.featuringCta']}
            />
          )}
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
